import React, { FC } from 'react';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import { TransitionProps } from '@mui/material/transitions';
import Slide from '@mui/material/Slide';
import makeStyles from '@mui/styles/makeStyles';
import { Box, Switch, Typography } from '@mui/material';
import { mainColors } from '../../../../../../styling/theme';
import { useRaidrKids } from '../../context/RaidrKidsContext';
import { useGetAffectedDocuments } from '../../../queries';
import { KidsData } from '../KidsEditor';
import LoadingButton from '../../../../../ui/LoadingButton';

interface SaveConfirmationDialogBoxProps {
  dialogIsShowing: boolean;
  setDialogIsShowing: (value: boolean) => void;
  getTemplateChangesMade: () => string[];
  getCanUserSave: () => string[];
  handleSaveChanges: (applyToAllDocuments: boolean) => void;
  figuresWereUpdated: boolean;
  handleUpdateFigures: (applyToAllShareClasses: boolean) => void;
  priipsKidsData: KidsData[];
}

const useStyles = makeStyles(() => ({
  switch: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareClassesContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    overlowY: 'scroll',
    maxHeight: '12.50rem',
  },
}));

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<any, any>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const SaveConfirmationDialogBox: FC<SaveConfirmationDialogBoxProps> = (
  props,
) => {
  const {
    dialogIsShowing,
    setDialogIsShowing,
    getTemplateChangesMade,
    getCanUserSave,
    handleSaveChanges,
    figuresWereUpdated,
    handleUpdateFigures,
    priipsKidsData,
  } = props;

  const { isPriips } = useRaidrKids();
  const getAffectedDocumentsMutation = useGetAffectedDocuments();

  // Check if any template changes were made to the document
  const changesMade = getTemplateChangesMade();
  const classes = useStyles();
  // Used to indicate if template changes should be applied to all documentsq
  const [applyToAllDocuments, setApplyToAllDocuments] =
    React.useState<boolean>(true);
  // Used to indicate if figures should be updated in all share classes
  const [applyToAllShareClasses, setApplyToAllShareClasses] =
    React.useState<boolean>(true);
  // Generate any error messages that will prevent the user from saving the document
  const errorMessages = getCanUserSave();
  // Used to indicate if the affected share class by template changes should be displayed
  const [showAffectedShareClass, setShowAffectedShareClass] =
    React.useState<boolean>(false);
  // Variable for storing affected share classes
  const [affectedShareClasses, setAffectedShareClasses] = React.useState<
    string[]
  >([]);
  // Function for displaying the share classes affected by template changes
  const displayAffectedShareClasses = async () => {
    const fieldIds = getTemplateChangesMade();
    if (!fieldIds.length) return [];

    const changes = fieldIds
      .map((id) => {
        const element = priipsKidsData.find((elem) => elem.fieldId === id);
        return element
          ? {
              template_id: element.templateId,
              text: element.content,
            }
          : null;
      })
      .filter(Boolean);

    const payload = {
      template_updates: changes,
    };

    try {
      const response = await getAffectedDocumentsMutation.mutateAsync(payload);
      setAffectedShareClasses(response.data);
      setShowAffectedShareClass(true);
    } catch (error) {
      setShowAffectedShareClass(false);
    }
  };
  return (
    <Dialog
      open={dialogIsShowing}
      TransitionComponent={Transition}
      keepMounted
      onClose={() => setDialogIsShowing(false)}
      aria-describedby="alert-dialog-slide-description"
      maxWidth="sm"
      fullWidth={true}
    >
      <DialogTitle textAlign="center">Confirm save latest version?</DialogTitle>

      {changesMade.length > 0 ? (
        <DialogContent>
          {showAffectedShareClass ? (
            <>
              <DialogContentText
                id="template-header-text"
                textAlign="center"
                style={{ fontSize: '0.75rem', fontWeight: 600 }}
              >
                The following share classes will be affected:
              </DialogContentText>
              <div className={classes.shareClassesContainer}>
                {affectedShareClasses.sort().map((shareClass, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor:
                        index % 2 === 0
                          ? mainColors.pastelBlue_lighter
                          : undefined,
                      width: '100%',
                    }}
                  >
                    <DialogContentText
                      id={`share-class-${index}`}
                      textAlign="center"
                      style={{ fontSize: '0.75rem', fontWeight: 600 }}
                    >
                      {shareClass}
                    </DialogContentText>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <DialogContentText
                id="template-header-text"
                textAlign="center"
                style={{ fontSize: '0.75rem', fontWeight: 600 }}
              >
                Template Changes were made. Click below to view affected
                documents:
              </DialogContentText>
              <Box textAlign={'center'} style={{ marginTop: '0.62rem' }}>
                <LoadingButton
                  isLoading={getAffectedDocumentsMutation.isPending}
                  className={`hover:bg-mainblue-dark flex items-center justify-center gap-2 rounded-sm bg-mainblue px-3 py-1 text-center text-white transition hover:bg-sky-800 focus:bg-sky-800 md:mx-auto`}
                  onClick={displayAffectedShareClasses}
                >
                  See Affected Documents
                </LoadingButton>
                {getAffectedDocumentsMutation.error && (
                  <DialogContentText
                    style={{
                      color: 'red',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      marginTop: '0.5rem',
                    }}
                  >
                    Failed to load affected documents. Please try again.
                  </DialogContentText>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      ) : null}
      {figuresWereUpdated ? (
        <DialogContent>
          <DialogContentText id="figures-header-text" textAlign="center">
            The figures were updated in this document
          </DialogContentText>
          <DialogContentText id="template-subheader-text" textAlign="center">
            Would you like to update figures in all other share classes of this
            fund?
          </DialogContentText>
          <div className={classes.switch}>
            <Typography>Just This Share Class</Typography>
            <Switch
              checked={applyToAllShareClasses}
              onChange={() =>
                setApplyToAllShareClasses(!applyToAllShareClasses)
              }
            />
            <Typography>All Share Class</Typography>
          </div>
        </DialogContent>
      ) : null}
      {isPriips && errorMessages.length ? (
        <>
          <DialogContentText id="error-message-header-text" textAlign="center">
            <Typography style={{ fontWeight: 600, color: 'red' }}>
              The following errors must be resolved before saving:
            </Typography>
          </DialogContentText>
          {errorMessages.map((message, index) => (
            <DialogContentText
              key={index}
              id={`error-message-${index}`}
              textAlign="center"
            >
              {message}
            </DialogContentText>
          ))}
        </>
      ) : null}
      <DialogActions>
        <Button onClick={() => setDialogIsShowing(false)}>Cancel</Button>
        <Button
          onClick={() => {
            handleSaveChanges(applyToAllDocuments);
            if (figuresWereUpdated) {
              handleUpdateFigures(applyToAllShareClasses);
            }
            setDialogIsShowing(false);
          }}
          disabled={isPriips && errorMessages.length > 0}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveConfirmationDialogBox;
